import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { PaginationService } from '../pagination.service';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { Attachment } from '../../objects/attachment';

// Add this interface to define the return type
export interface ProcessedAttachment {
  isOfficeDocument: boolean;
  safePdfHtml: SafeHtml | null;
  directLink: string | null;
  officeFileLink?: string;
  safeOfficeFileLink: SafeResourceUrl | null;
}

@Injectable({
  providedIn: 'root'
})
export class AttachmentsService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  private sanitizer = inject(DomSanitizer);

  private allAttachmentsSubject = new BehaviorSubject<any[]>([]);
  public allAttachments$ = this.allAttachmentsSubject.asObservable();

  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();


  /**
   * Retrieves an array of attachments by their attachment numbers.
   * This is useful when you have a specific list of attachments you need to fetch.
   * This function requires a new API endpoint that can handle a list of IDs,
   * likely using a DynamoDB BatchGetItem operation for efficiency.
   * @param attachmentNums An array of strings representing the unique attachment numbers.
   * @returns An Observable of an array of Attachment objects.
   */
  getAttachmentsByNumbers(attachmentNums: string[] | null | undefined): Observable<Attachment[]> {
    // Check for null, undefined, or an empty array
    if (!attachmentNums || attachmentNums.length === 0) {
      console.debug('AttachmentsService: getAttachmentsByNumbers called with no valid attachment numbers. Returning an empty array.');
      return of([]); // Use 'of' to return a cold observable of an empty array
    }

    // Backend expects { attachmentIds: [...] } as the field name
    return this.http.post<Attachment[]>(environment.URL_ATTACHMENTS_GET_BY_NUMBERS, { attachmentIds: attachmentNums });
  }


  /**
   * Retrieves all attachments associated with a single entity.
   *
   * API: GET api/{org}/attachments/by-entity/{entityId}
   * Returns flat attachment objects (no wrapper)
   *
   * @param parentEntityId The unique identifier of the parent entity (e.g., contractId).
   * @param entityType (Optional) Not used - kept for API compatibility.
   * @returns An Observable of an array of Attachment objects.
   */
  getAttachmentsForEntity(parentEntityId: string, _entityType?: string): Observable<Attachment[]> {
    // URL already has trailing slash, just append the entityId
    const url = environment.URL_ATTACHMENTS_GET_BY_ENTITY + parentEntityId;

    return this.http.get<any>(url).pipe(
      map((response: any) => {
        // Handle both array and wrapped formats
        if (Array.isArray(response)) {
          return response.map(item => item.attachmentData || item);
        }
        if (response?.items) {
          return response.items.map((item: any) => item.attachmentData || item);
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching attachments for entity:', error);
        return of([]);
      })
    );
  }




  /**
   * Saves the attachment metadata to the backend API.
   *
   * NEW API (Phase 1):
   * - POST api/{org}/attachments
   * - Body: flat data with parentEntityId
   * - If no attachmentId: CREATE - backend generates ID (format ATT-xxx)
   * - If has attachmentId: UPDATE
   * - Returns { attachmentId, message }
   *
   * @param attachmentObject The attachment object containing metadata (like s3filename, directLink, parentEntityId)
   * @returns An observable of the API response containing attachmentId.
   */
  saveAttachment(attachmentObject: any): Observable<any> {
    const url = environment.URL_ATTACHMENTS_SAVE_ATTACHMENT;

    // NEW API: POST with flat data (no wrapper)
    return this.http.post(url, attachmentObject).pipe(
      tap(response => console.log('Attachment metadata saved successfully:', response)),
      catchError(error => {
        console.error('Error saving attachment metadata:', error);
        throw error; // Re-throw the error to be handled by the component
      })
    );
  }

  /**
   * Encapsulates the logic for filtering a list of attachments to only include
   * those with letters in their names.
   * @param attachmentList The raw list of attachments.
   * @returns A filtered list of attachments.
   */
  filterAttachmentListForLetters(attachmentList: any[]): any[] {
    // You can combine the two filter calls into one for better readability and efficiency.
    return attachmentList.filter(attachment =>
      attachment.name && /[a-zA-Z]/.test(attachment.name)
    );
  }

  /**
   * Refreshes all attachments by calling the paginated API and updating the subject.
   * Excludes main contract entity files by default.
   */
  refreshAllAttachmentsRecords(): void {
    this.isCacheRefreshedSubject.next(false);

    this.paginationService.getAllPaginatedData(key => this.getPageOfAttachments(key))
      .pipe(
        map(allAttachments => allAttachments.map(item => this.flattenAttachment(item))),
        tap(allAttachments => console.log('Successfully fetched all attachments:', allAttachments.length)),
        catchError(error => {
          console.error('Error fetching all attachments:', error);
          this.allAttachmentsSubject.next([]);
          this.isCacheRefreshedSubject.next(true);
          return of([]);
        })
      )
      .subscribe(allAttachments => {
        this.allAttachmentsSubject.next(allAttachments);
        this.isCacheRefreshedSubject.next(true);
      });
  }

  /**
   * Flattens an attachment from the API wrapper format to a flat object.
   * API returns { attachmentData: {...}, attachmentNum: "..." }
   * Components receive { name, description, attachmentNum, ... }
   */
  private flattenAttachment(attachment: any): Attachment {
    if (!attachment) return attachment;

    if (attachment.attachmentData) {
      const { attachmentData, ...metadata } = attachment;
      return { ...attachmentData, ...metadata } as Attachment;
    }

    return attachment as Attachment;
  }

  private getPageOfAttachments(lastEvaluatedKey: any = null): Observable<any> {
    const body: any = {
      lastEvaluatedKey,
      excludeMainEntityFiles: true
    };

    return this.http.post(environment.URL_ATTACHMENTS_PAGE_OF_ATTACHMENTS, body).pipe(
      map((response: any) => ({
        Items: response.data || response.items || [],
        lastEvaluatedKey: response.LastEvaluatedKey || response.lastEvaluatedKey || null
      })),
      tap(response => console.log('Fetched attachments page:', response.Items?.length, 'items')),
      catchError(error => {
        console.error('Error fetching attachments page:', error);
        return of({ Items: [], lastEvaluatedKey: null });
      })
    );
  }

  /**
   * Processes a contract file attachment to determine its type and return safe
   * links and HTML for display.
   * @param mainContractFileName The S3 filename of the attachment.
   * @returns An Observable of the processed attachment data.
   */
  public processMainContractFileAttachment(mainContractFileName: string): Observable<ProcessedAttachment> {
    const fileExtension = mainContractFileName.split('.').pop()?.toLowerCase() || '';

    const attachment: ProcessedAttachment = {
      isOfficeDocument: false,
      safePdfHtml: null,
      directLink: `${environment.s3Location}${mainContractFileName}`,
      safeOfficeFileLink: null,
    };

    switch (fileExtension) {
      case 'pdf':
      case 'htm':
      case 'html':
        attachment.isOfficeDocument = false;
        attachment.safePdfHtml = this.sanitizer.bypassSecurityTrustHtml(
          `<object width="700" height="1000" data="${attachment.directLink}" type="application/pdf">` +
          `<embed src="${attachment.directLink}" type="application/pdf" /></object>`
        );
        break;

      case 'doc':
      case 'docx':
      case 'ppt':
      case 'pptx':
      case 'xls':
      case 'xlsx':
        attachment.isOfficeDocument = true;
        const officeBaseUrl = fileExtension.startsWith('doc') || fileExtension.startsWith('ppt') ?
          'https://view.officeapps.live.com/op/embed.aspx?src=' :
          'https://excel.officeapps.live.com/op/embed.aspx?src=';

        attachment.officeFileLink = `${officeBaseUrl}${attachment.directLink}`;
        attachment.safeOfficeFileLink = this.sanitizer.bypassSecurityTrustResourceUrl(attachment.officeFileLink);
        break;

      case 'msg':
        attachment.isOfficeDocument = false;
        attachment.safePdfHtml = this.sanitizer.bypassSecurityTrustHtml(
          `<a href="${attachment.directLink}" download="${mainContractFileName}" target="_new">View Message</a>`
        );
        break;
    }

    return of(attachment);
  }
}
